import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Copy, Wallet, Upload, CheckCircle, Clock, Eye, EyeOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import { useBalanceVisibility } from '../contexts/BalanceVisibilityContext';
import HoldingsDisplay from '../components/HoldingsDisplay';
import CryptoIcon from '../components/CryptoIcon';
import { getNetworkOptionsForCoin, matchesNetwork } from '../constants/networks';
import axios from 'axios';

const API_URL = '';

const Recharge = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { balanceVisible, toggleBalanceVisibility, formatBalance } = useBalanceVisibility();
  
  const [balance, setBalance] = useState(null);
  const [selectedCoin, setSelectedCoin] = useState('');
  const [selectedNetworkOption, setSelectedNetworkOption] = useState('');
  const [selectedCrypto, setSelectedCrypto] = useState(null);
  const [amount, setAmount] = useState('');
  const [screenshot, setScreenshot] = useState(null);
  const [screenshotPreview, setScreenshotPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [recentDeposits, setRecentDeposits] = useState([]);
  const [cryptoAddresses, setCryptoAddresses] = useState([]);
  const [loadingAddresses, setLoadingAddresses] = useState(true);
  const [notifications, setNotifications] = useState([]);

  useEffect(() => {
    fetchBalance();
    fetchRecentDeposits();
    fetchCryptoAddresses();
    fetchNotifications();
    
    // Auto-refresh balance every 60 seconds for live price updates
    const balanceInterval = setInterval(fetchBalance, 60000);
    return () => clearInterval(balanceInterval);
  }, []);

  // Build a unique, ordered list of coin codes available for deposit
  const availableCoins = Array.from(
    new Set(cryptoAddresses.map(a => (a.code || '').toUpperCase()).filter(Boolean))
  );

  // When the coin changes, default the network selection (BTC → BTC, others → keep current or clear)
  useEffect(() => {
    if (!selectedCoin) {
      setSelectedNetworkOption('');
      return;
    }
    const options = getNetworkOptionsForCoin(selectedCoin);
    if (options.length === 1) {
      // BTC – auto-select BTC network
      setSelectedNetworkOption(options[0]);
    } else if (!options.includes(selectedNetworkOption)) {
      // Reset network when coin changes to a non-matching option
      setSelectedNetworkOption('');
    }
  }, [selectedCoin]);

  // Resolve the actual platform deposit address for the chosen (coin, network)
  useEffect(() => {
    if (!selectedCoin || !selectedNetworkOption) {
      setSelectedCrypto(null);
      return;
    }
    const match = cryptoAddresses.find(
      a => (a.code || '').toUpperCase() === selectedCoin &&
           matchesNetwork(a.network, selectedNetworkOption)
    );
    setSelectedCrypto(match || null);
  }, [selectedCoin, selectedNetworkOption, cryptoAddresses]);

  const fetchCryptoAddresses = async () => {
    try {
      setLoadingAddresses(true);
      const token = localStorage.getItem('token');
      
      // Fetch user-specific deposit addresses (custom if set, otherwise platform defaults)
      const response = await axios.get(`${API_URL}/api/deposits/my-deposit-addresses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setCryptoAddresses(response.data.addresses || []);
      }
    } catch (error) {
      console.error('Error fetching crypto addresses:', error);
      toast({
        title: "Error",
        description: "Failed to load crypto addresses. Please refresh the page.",
        variant: "destructive"
      });
    } finally {
      setLoadingAddresses(false);
    }
  };

  const fetchBalance = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/wallet/balance`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Store the balances object which has individual crypto amounts
      setBalance(response.data.balances || {});
    } catch (error) {
      console.error('Error fetching balance:', error);
    }
  };

  const fetchRecentDeposits = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/deposits/my-deposits`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setRecentDeposits(response.data.deposits || []);
      }
    } catch (error) {
      console.error('Error fetching deposits:', error);
    }
  };

  const fetchNotifications = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/notifications/deposit-notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        // Get only unread notifications or latest 3
        const unreadNotifs = response.data.notifications.filter(n => n.status === 'unread').slice(0, 3);
        console.log('🔔 Fetched notifications:', unreadNotifs.map(n => ({ type: n.type, title: n.title })));
        setNotifications(unreadNotifs);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  const markNotificationAsRead = async (notificationId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/notifications/mark-read/${notificationId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Remove from local state
      setNotifications(notifications.filter(n => n.notification_id !== notificationId));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleScreenshotChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast({
          title: 'Error',
          description: 'Please select an image file',
          variant: 'destructive'
        });
        return;
      }
      
      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: 'Error',
          description: 'Image size should be less than 5MB',
          variant: 'destructive'
        });
        return;
      }

      setScreenshot(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setScreenshotPreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async () => {
    if (!selectedCrypto) {
      toast({
        title: 'Error',
        description: 'Please select a cryptocurrency',
        variant: 'destructive'
      });
      return;
    }

    if (!amount || parseFloat(amount) <= 0) {
      toast({
        title: 'Error',
        description: 'Please enter a valid amount',
        variant: 'destructive'
      });
      return;
    }

    if (!screenshot) {
      toast({
        title: 'Error',
        description: 'Please upload payment screenshot',
        variant: 'destructive'
      });
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('currency', selectedCrypto.code);
      formData.append('network', selectedCrypto.network);
      formData.append('amount', amount);
      formData.append('wallet_address', selectedCrypto.address);
      formData.append('screenshot', screenshot);

      const response = await axios.post(
        `${API_URL}/api/deposits/create-with-screenshot`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data.success) {
        toast({
          title: 'Success!',
          description: 'Deposit request submitted. Admin will credit your account after verification.'
        });
        
        // Reset form
        setSelectedCrypto(null);
        setAmount('');
        setScreenshot(null);
        setScreenshotPreview(null);
        
        // Refresh deposits
        fetchRecentDeposits();
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to submit deposit',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text, name) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text)
        .then(() => {
          toast({
            title: 'Copied!',
            description: `${name} address copied to clipboard`
          });
        })
        .catch(err => {
          // Fallback for older browsers
          const textArea = document.createElement('textarea');
          textArea.value = text;
          textArea.style.position = 'fixed';
          textArea.style.left = '-9999px';
          document.body.appendChild(textArea);
          textArea.select();
          try {
            document.execCommand('copy');
            toast({
              title: 'Copied!',
              description: `${name} address copied to clipboard`
            });
          } catch (err) {
            toast({
              title: 'Copy Failed',
              description: 'Please manually copy the address',
              variant: 'destructive'
            });
          }
          document.body.removeChild(textArea);
        });
    } else {
      // Fallback for browsers without clipboard API
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-9999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        toast({
          title: 'Copied!',
          description: `${name} address copied to clipboard`
        });
      } catch (err) {
        toast({
          title: 'Copy Failed',
          description: 'Please manually copy the address',
          variant: 'destructive'
        });
      }
      document.body.removeChild(textArea);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-950 via-cyan-900 to-blue-900 pb-20">
      {/* Header */}
      <div className="sticky top-0 bg-gradient-to-r from-blue-900 via-cyan-800 to-blue-900 backdrop-blur-sm border-b border-cyan-500/30 z-10 shadow-lg">
        <div className="flex items-center justify-between px-4 py-4 max-w-4xl mx-auto">
          <button 
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-white hover:text-cyan-300 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-semibold">Back</span>
          </button>
          <h1 className="text-lg font-bold text-white">Deposit Funds</h1>
          <div className="w-16"></div>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 py-6 max-w-4xl mx-auto">
        {/* Balance Card - Using HoldingsDisplay for consistency */}
        <HoldingsDisplay 
          showTotal={true}
          showIndividual={false}
          showLastUpdate={false}
          showRefreshButton={true}
          title="Current Portfolio Balance"
          className="mb-6"
          compact={true}
        />

        {/* Main Deposit Section */}
        <div className="bg-gradient-to-br from-cyan-600/10 to-blue-600/10 backdrop-blur-sm border border-cyan-500/30 rounded-lg p-6 shadow-xl">
          <h2 className="text-xl font-bold text-white mb-6">Make a Deposit</h2>
          
          {/* Step 1: Select Crypto */}
          <div className="mb-6 pb-6 border-b border-cyan-500/20">
            <label className="block text-sm font-semibold text-white mb-3">1. Select Cryptocurrency</label>
            {loadingAddresses ? (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-cyan-400 border-t-transparent"></div>
                <p className="text-cyan-300 mt-2">Loading crypto addresses...</p>
              </div>
            ) : cryptoAddresses.length === 0 ? (
              <div className="text-center py-8 bg-yellow-900/20 border border-yellow-500/30 rounded-lg">
                <p className="text-yellow-300 font-semibold">No crypto addresses available</p>
                <p className="text-yellow-200 text-sm mt-1">Please contact support or check back later</p>
              </div>
            ) : (
              <div className="relative">
                <select
                  data-testid="recharge-coin-select"
                  value={selectedCoin}
                  onChange={(e) => setSelectedCoin(e.target.value)}
                  className="w-full pl-14 pr-10 py-4 bg-slate-800/50 border border-cyan-500/30 text-cyan-100 rounded-lg focus:border-cyan-500 focus:outline-none appearance-none cursor-pointer"
                  style={{
                    backgroundImage: `linear-gradient(45deg, transparent 50%, currentColor 50%), linear-gradient(135deg, currentColor 50%, transparent 50%)`,
                    backgroundPosition: 'calc(100% - 20px) calc(1em + 2px), calc(100% - 15px) calc(1em + 2px)',
                    backgroundSize: '5px 5px, 5px 5px',
                    backgroundRepeat: 'no-repeat'
                  }}
                >
                  <option value="">Select a cryptocurrency...</option>
                  {availableCoins.map((coin) => (
                    <option key={coin} value={coin}>{coin}</option>
                  ))}
                </select>
                {selectedCoin && (
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none">
                    <CryptoIcon symbol={selectedCoin} size={24} />
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Step 1b: Select Network */}
          {selectedCoin && (
            <div className="mb-6 pb-6 border-b border-cyan-500/20">
              <label className="block text-sm font-semibold text-white mb-3">2. Select Network</label>
              <select
                data-testid="recharge-network-select"
                value={selectedNetworkOption}
                onChange={(e) => setSelectedNetworkOption(e.target.value)}
                className="w-full px-4 py-4 bg-slate-800/50 border border-cyan-500/30 text-cyan-100 rounded-lg focus:border-cyan-500 focus:outline-none cursor-pointer"
              >
                <option value="">Select a network...</option>
                {getNetworkOptionsForCoin(selectedCoin).map((net) => (
                  <option key={net} value={net}>{net}</option>
                ))}
              </select>

              {selectedNetworkOption && !selectedCrypto && (
                <div
                  data-testid="recharge-network-unavailable"
                  className="mt-3 px-4 py-3 bg-yellow-900/30 border border-yellow-500/40 rounded-lg text-yellow-200 text-sm"
                >
                  Deposit on this network not yet available, please contact support.
                </div>
              )}

              {selectedCrypto && (
                  <div className="mt-3 px-4 py-3 bg-slate-800/50 rounded-lg border border-slate-700">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <CryptoIcon symbol={selectedCrypto.code} size={24} />
                      <div>
                        <div className="text-sm font-semibold text-cyan-400">
                          {(selectedCrypto.code || '').toUpperCase()} ({selectedNetworkOption})
                        </div>
                        <div className="text-xs text-gray-400">Selected cryptocurrency</div>
                      </div>
                    </div>
                    <CheckCircle className="w-5 h-5 text-cyan-400" />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 2: Copy Address */}
          {selectedCrypto && (
            <>
              <div className="mb-6 pb-6 border-b border-cyan-500/20">
                <label className="block text-sm font-semibold text-white mb-3">
                  3. Copy {selectedCrypto.name} Address
                </label>
                <div className="bg-blue-900/40 border border-cyan-500/30 rounded-lg p-4">
                  <div className="text-xs text-cyan-300 mb-2">
                    Network: <span className="font-semibold text-white">{selectedNetworkOption}</span>
                  </div>
                  <div className="bg-black/20 border border-cyan-500/20 rounded p-3 mb-3">
                    <div className="font-mono text-sm text-cyan-100 break-all">
                      {selectedCrypto.address}
                    </div>
                  </div>
                  <button
                    onClick={() => copyToClipboard(selectedCrypto.address, selectedCrypto.name)}
                    className="w-full sm:w-auto px-6 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-semibold rounded-lg flex items-center justify-center gap-2 transition-all shadow-lg shadow-cyan-500/30"
                  >
                    <Copy className="w-4 h-4" />
                    Copy Address
                  </button>
                  <div className="text-xs text-yellow-400 mt-3 flex items-center gap-1">
                    <span>⚠️</span>
                    <span>Send on {selectedNetworkOption} only</span>
                  </div>
                </div>
              </div>

              {/* Step 4: Amount */}
              <div className="mb-6 pb-6 border-b border-cyan-500/20">
                <label className="block text-sm font-semibold text-white mb-3">
                  4. Enter Amount ({(selectedCrypto?.code || 'USD').toUpperCase()})
                </label>
                <input
                  type="number"
                  placeholder="0.00"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="w-full px-4 py-3 bg-blue-900/40 border border-cyan-500/30 rounded-lg text-white placeholder-cyan-700 focus:border-cyan-400 focus:ring-2 focus:ring-cyan-500/50 focus:outline-none"
                />
                {selectedCrypto && (
                  <div className="mt-2 text-xs text-cyan-300">
                    Enter the amount in {selectedCrypto.code} you wish to deposit
                  </div>
                )}
              </div>

              {/* Step 5: Upload Screenshot */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-white mb-3">
                  5. Upload Payment Screenshot
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleScreenshotChange}
                  className="hidden"
                  id="screenshot-upload"
                />
                <label
                  htmlFor="screenshot-upload"
                  className="block w-full border-2 border-dashed border-cyan-500/40 rounded-lg p-8 text-center cursor-pointer hover:border-cyan-400/60 hover:bg-cyan-500/5 transition-all"
                >
                  {screenshotPreview ? (
                    <div className="space-y-3">
                      <img src={screenshotPreview} alt="Preview" className="max-h-48 mx-auto rounded" />
                      <div className="flex items-center justify-center gap-2 text-green-400 text-sm font-semibold">
                        <CheckCircle className="w-5 h-5" />
                        Screenshot uploaded
                      </div>
                    </div>
                  ) : (
                    <>
                      <Upload className="w-12 h-12 mx-auto text-cyan-400 mb-3" />
                      <div className="text-white font-medium mb-1">Click to upload screenshot</div>
                      <div className="text-cyan-300 text-sm">PNG or JPG (max 5MB)</div>
                    </>
                  )}
                </label>
              </div>

              {/* Confirm Button */}
              <button
                onClick={handleSubmit}
                disabled={loading || !amount || !screenshot}
                className="w-full py-4 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-400 hover:to-green-500 text-white font-bold rounded-lg transition-all shadow-lg shadow-green-500/30 disabled:from-gray-600 disabled:to-gray-700 disabled:cursor-not-allowed disabled:shadow-none"
              >
                {loading ? 'Processing...' : 'Confirm Deposit'}
              </button>
              
              <p className="text-cyan-300 text-xs mt-3 text-center">
                Admin will verify and credit your account after confirmation
              </p>
            </>
          )}
        </div>

        {/* Deposit Notifications */}
        {notifications.length > 0 && (
          <div className="mt-6 bg-gradient-to-br from-purple-600/10 to-pink-600/10 backdrop-blur-sm border border-purple-500/30 rounded-lg p-6 shadow-xl">
            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-purple-400" />
              Deposit Notifications
            </h3>
            <div className="space-y-3">
              {notifications.map((notif) => (
                <div 
                  key={notif.notification_id} 
                  className={`relative p-4 rounded-lg border ${
                    notif.type === 'deposit_approved' 
                      ? 'bg-green-900/20 border-green-500/30' 
                      : 'bg-red-900/20 border-red-500/30'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        {notif.type === 'deposit_approved' ? (
                          <CheckCircle className="w-5 h-5 text-green-400" />
                        ) : (
                          <Clock className="w-5 h-5 text-red-400" />
                        )}
                        <h4 className={`font-semibold ${
                          notif.type === 'deposit_approved' ? 'text-green-300' : 'text-red-300'
                        }`}>
                          {notif.title}
                        </h4>
                      </div>
                      <p className="text-sm text-gray-300 mb-2">{notif.message}</p>
                      <div className="text-xs text-gray-400">
                        {new Date(notif.created_at).toLocaleString()}
                      </div>
                    </div>
                    <button
                      onClick={() => markNotificationAsRead(notif.notification_id)}
                      className="text-gray-400 hover:text-white transition-colors text-xl leading-none"
                      title="Dismiss"
                    >
                      ✕
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recent Deposits - Only Pending */}
        {recentDeposits.filter(d => d.status === 'pending').length > 0 && (
          <div className="mt-6 bg-gradient-to-br from-cyan-600/10 to-blue-600/10 backdrop-blur-sm border border-cyan-500/30 rounded-lg p-6 shadow-xl">
            <h3 className="text-lg font-bold text-white mb-4">Recent Pending Deposits</h3>
            <div className="space-y-3">
              {recentDeposits.filter(d => d.status === 'pending').slice(0, 5).map((deposit) => (
                <div key={deposit.id || deposit.deposit_id} className="flex items-center justify-between p-4 bg-blue-900/30 border border-cyan-500/20 rounded-lg hover:bg-blue-900/40 transition-colors">
                  <div className="flex items-center gap-3">
                    <CryptoIcon symbol={deposit.currency} size={32} />
                    <div>
                      <div className="font-semibold text-white">{parseFloat(deposit.amount).toFixed(8)} {(deposit.currency || '').toUpperCase()}</div>
                      <div className="text-xs text-gray-400">Deposit Amount</div>
                    </div>
                  </div>
                  <div className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    deposit.status === 'approved' || deposit.status === 'confirmed'
                      ? 'bg-green-500/20 text-green-300 border border-green-500/50'
                      : deposit.status === 'pending'
                      ? 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/50'
                      : 'bg-red-500/20 text-red-300 border border-red-500/50'
                  }`}>
                    {deposit.status === 'approved' || deposit.status === 'confirmed' ? 'Approved' : 
                     deposit.status === 'pending' ? 'Pending' : 'Rejected'}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Recharge;